import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import NotFound from '../ul/NotFound';

const LoginChart = ({ data, title = 'Logins Over Time' }) => {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);

    const hasData = data && data.length > 0;

    useEffect(() => {
        if (!hasData) return;

        if (chartInstance.current) {
            chartInstance.current.dispose();
            chartInstance.current = null;
        }

        if (!chartRef.current) return;

        // Count logins per date
        const loginCounts = data.reduce((acc, entry) => {
            const date = entry.createdAt.split('T')[0];
            acc[date] = (acc[date] || 0) + 1;
            return acc;
        }, {});

        // Convert to sorted array
        const sortedData = Object.entries(loginCounts)
            .map(([date, count]) => [new Date(date).getTime(), count])
            .sort((a, b) => a[0] - b[0]);

        if (sortedData.length === 0) return;

        const chart = echarts.init(chartRef.current);
        chartInstance.current = chart;

        const option = {
            title: {
                text: title,
                left: 'center',
                textStyle: {
                    color: '#333',
                    fontSize: 15,
                    fontWeight: 600,
                },
            },
            tooltip: {
                trigger: 'axis',
                backgroundColor: '#fff',
                borderColor: '#eab308',
                textStyle: { color: '#333', fontSize: 12 },
                formatter: (params) => {
                    const date = new Date(params[0].value[0]);
                    const day = String(date.getDate()).padStart(2, '0');
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    return `${day}/${month} — <b>${params[0].value[1]}</b> logins`;
                },
            },
            grid: {
                left: '10%',
                right: '5%',
                bottom: '12%',
                top: '18%',
            },
            xAxis: {
                type: 'time',
                boundaryGap: false,
                axisLine: { lineStyle: { color: '#ddd' } },
                axisLabel: {
                    color: '#666',
                    fontSize: 11,
                    formatter: (value) => {
                        const date = new Date(value);
                        return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}`;
                    },
                },
                splitLine: { show: false },
            },
            yAxis: {
                type: 'value',
                axisLine: { show: false },
                axisLabel: { color: '#666', fontSize: 11 },
                splitLine: { lineStyle: { color: '#f0f0f0' } },
            },
            series: [
                {
                    name: 'Logins',
                    type: 'line',
                    smooth: true,
                    symbol: 'circle',
                    symbolSize: 8,
                    showSymbol: true,
                    lineStyle: { color: '#eab308', width: 2 },
                    itemStyle: { color: '#eab308', borderColor: '#fff', borderWidth: 2 },
                    areaStyle: {
                        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                            { offset: 0, color: 'rgba(234, 179, 8, 0.25)' },
                            { offset: 1, color: 'rgba(234, 179, 8, 0.02)' },
                        ]),
                    },
                    data: sortedData,
                },
            ],
        };

        chart.setOption(option);

        const handleResize = () => chartInstance.current?.resize();
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            chartInstance.current?.dispose();
            chartInstance.current = null;
        };
    }, [hasData, data]);

    return (
        <div className="bg-white border border-yellow-200 p-4 rounded-lg mt-4 shadow-sm">
            {!hasData ? (
                <div className="flex items-center justify-center min-h-[300px]">
                    <NotFound />
                </div>
            ) : (
                <div ref={chartRef} style={{ width: '100%', height: '300px' }} />
            )}
        </div>
    );
};

export default LoginChart;